import React, { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import { useSearchParams } from "react-router-dom";
import { StaffDataTable } from "../../Components/Staff/StaffDataTable";
import { StaffFirst } from "../../Components/Staff/StaffFirst";
import { colors } from "../../Shared/colors";
import { STAFF_STATUS } from "../../Shared/staffStatus";

/**
 * Manage Staff.
 *
 * Filter state lives here because the controls and the table are siblings.
 * Previously the four filter fields had no state, no onChange and no submit —
 * typing in them did nothing and the table never changed. They were decorative.
 *
 * The status dimension is separate from the rest of the filters and lives in
 * the URL: which slice of the roster you are looking at is the single most
 * important thing about this screen, so it has to survive a refresh, come back
 * with the browser Back button, and be the same view for whoever you send the
 * link to. The other filters stay in memory — they are a scratch query, not a
 * place in the app.
 */
const EMPTY_FILTERS = { search: "", role: "", region: "" };

const TAB_VALUES = [
  STAFF_STATUS.ACTIVE,
  STAFF_STATUS.BLOCKED,
  STAFF_STATUS.REMOVED,
];

export const StaffComponent = () => {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [searchParams, setSearchParams] = useSearchParams();

  // An unrecognised ?tab= falls back to Active rather than showing an empty
  // table, so a mistyped or stale link still lands somewhere useful.
  const requested = searchParams.get("tab");
  const tab = useMemo(
    () => (TAB_VALUES.includes(requested) ? requested : STAFF_STATUS.ACTIVE),
    [requested]
  );

  const setTab = useCallback(
    (next) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === STAFF_STATUS.ACTIVE) {
            // Active is the default view, so it stays out of the URL.
            params.delete("tab");
          } else {
            params.set("tab", next);
          }
          // Page 7 of Active is not page 7 of Blocked. Carrying the page number
          // across a tab change lands you on an empty table.
          params.delete("page");
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  return (
    <Main>
      <Inner>
        <StaffFirst
          filters={filters}
          onFiltersChange={setFilters}
          onClearFilters={() => setFilters(EMPTY_FILTERS)}
        />
        <StaffDataTable filters={filters} tab={tab} onTabChange={setTab} />
      </Inner>
    </Main>
  );
};

const Main = styled.div`
  background-color: ${colors.inputWhite};
  min-height: calc(100vh - 10vh);
  margin-top: 10vh;
  padding: 1.5rem 0 2.5rem 0;
`;

/**
 * One column, one gap. The page previously centred its children with flex and
 * let each child bring its own margins, so the vertical rhythm changed between
 * the header, the filters and the table.
 */
const Inner = styled.div`
  width: 100%;
  max-width: 1500px;
  margin: 0 auto;
  padding: 0 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;
